import React, { Component, useMemo } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
  Table,
  Spinner,
  Input,
  Button,
  Badge,
  TabContent,
  TabPane,
} from "reactstrap";
import { listAllSensitivities, updateSensitivity, SENSITIVITY_TYPES } from "../services/sensitivityService";
import AdminOnlyAccess from "../components/common/base/AdminOnlyAccess";
import { toast } from "react-toastify";

function validateRows(rows) {
  const errorsById = new Map();
  rows.forEach((row) => {
    if (row.level_value_multiplier != null && Number(row.level_value_multiplier) <= 0) {
      errorsById.set(row.id, ["level_value_multiplier must be > 0"]);
    }
  });
  return errorsById;
}

function SensitivityTable({ title, rows, onChange, onSave }) {
  const errorsById = useMemo(() => validateRows(rows), [rows]);

  return (
    <Card className="mb-4">
      <CardHeader>
        <h5 className="card-category">{title}</h5>
      </CardHeader>
      <CardBody>
        <Table responsive bordered size="sm" className="w-100">
          <thead className="text-primary">
            <tr>
              <th>Level</th>
              <th>level_value_multiplier</th>
              <th>is_default</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const rowErrors = errorsById.get(row.id) || [];
              const hasErrors = rowErrors.length > 0;
              return (
                <tr key={row.id} className={hasErrors ? "table-danger" : undefined}>
                  <td>{row.level}</td>
                  <td>
                    <Input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      value={row.level_value_multiplier ?? ""}
                      onChange={(e) => onChange(row.id, { level_value_multiplier: e.target.value })}
                    />
                  </td>
                  <td className="text-center">
                    <Input
                      type="checkbox"
                      checked={!!row.is_default}
                      onChange={(e) => {
                        if (e.target.checked) {
                          onChange(row.id, { is_default: true });
                        }
                      }}
                    />
                  </td>
                  <td>
                    <Button size="sm" color="success" disabled={hasErrors} onClick={() => onSave(row)}>
                      Save
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <div>
          {rows.map((row) => {
            const rowErrors = errorsById.get(row.id) || [];
            return (
              rowErrors.length > 0 && (
                <div key={`err-${row.id}`} className="mb-1">
                  <Badge color="danger" className="mr-2">
                    Level {row.level}
                  </Badge>
                  <span>{rowErrors.join("; ")}</span>
                </div>
              )
            );
          })}
        </div>
      </CardBody>
    </Card>
  );
}

class SensitivityConfigView extends Component {
  state = {
    sensitivities: [],
    activeTypeIndex: 0,
    loading: true,
  };

  async componentDidMount() {
    await this.fetchSensitivities();
  }

  getGroupedSensitivities() {
    const { sensitivities } = this.state;
    if (this.groupedSource !== sensitivities) {
      this.groupedSource = sensitivities;
      this.groupedCache = SENSITIVITY_TYPES.map(({ key, label }) => ({
        key,
        label,
        rows: sensitivities.filter((r) => r.sensitivity_type === key).sort((a, b) => a.level - b.level),
      }));
    }
    return this.groupedCache;
  }

  toggle = (typeIndex) => {
    const { activeTypeIndex } = this.state;
    if (activeTypeIndex !== typeIndex) {
      this.setState({ activeTypeIndex: typeIndex });
    }
  };

  onChange = (id, patch) => {
    this.setState((prev) => {
      const current = prev.sensitivities.find((r) => r.id === id);
      let next = prev.sensitivities.map((r) => (r.id === id ? { ...r, ...patch } : r));
      if (Object.prototype.hasOwnProperty.call(patch, "is_default") && current) {
        if (patch.is_default) {
          next = next.map((r) => {
            if (r.sensitivity_type === current.sensitivity_type && r.id !== id) {
              return { ...r, is_default: false };
            }
            return r;
          });
        }
      }
      return { sensitivities: next };
    });
  };

  onSave = async (row) => {
    const num = (v) => (v === "" || v == null ? null : Number(v));
    const payload = {
      level_value_multiplier: num(row.level_value_multiplier),
      is_default: !!row.is_default,
    };
    try {
      await updateSensitivity(row.id, payload);
      toast.success("Saved sensitivity");
    } catch (e) {
      toast.error("Save failed");
    }
  };

  async fetchSensitivities() {
    try {
      const { data } = await listAllSensitivities();
      this.setState({ sensitivities: data || [] });
    } catch (e) {
      toast.error("Failed to load sensitivities");
    } finally {
      this.setState({ loading: false });
    }
  }

  render() {
    const { activeTypeIndex, loading } = this.state;
    const grouped = this.getGroupedSensitivities();

    return (
      <AdminOnlyAccess>
        <div className="content">
          <Card>
            <CardHeader>
              <h5 className="card-category">Sensitivity configs</h5>
            </CardHeader>
            {loading && (
              <CardBody>
                <Spinner color="primary" />
              </CardBody>
            )}
            {!loading && (
              <CardBody>
                <Nav tabs>
                  {grouped.map((g, idx) => (
                    <NavItem role="button" key={g.key}>
                      <NavLink className={activeTypeIndex === idx ? "active" : ""} onClick={() => this.toggle(idx)}>
                        <p>{g.label}</p>
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
                <TabContent activeTab={activeTypeIndex}>
                  {grouped.map((g, idx) => (
                    <TabPane key={g.key} tabId={idx}>
                      <SensitivityTable
                        title={`${g.label} sensitivities`}
                        rows={g.rows}
                        onChange={this.onChange}
                        onSave={this.onSave}
                      />
                    </TabPane>
                  ))}
                </TabContent>
              </CardBody>
            )}
          </Card>
        </div>
      </AdminOnlyAccess>
    );
  }
}

export default SensitivityConfigView;
